import { useEffect, useRef } from "react";
import { Location } from "react-router-dom";

/**
 * Custom hook to handle clearing table state when navigating from create action (not edit).
 * This hook detects the `clearState` flag in navigation state and resets the table state accordingly.
 * 
 * @param location - The location object from useLocation() hook
 * @param resetState - Function to reset the table state (from usePreservedTableState)
 * 
 * @example
 * ```tsx
 * const location = useLocation();
 * const { resetState } = usePreservedTableState({ key: "item" });
 * 
 * useClearStateOnNavigation(location, resetState);
 * ```
 */
export default function useClearStateOnNavigation(
  location: Location,
  resetState: () => void
) {
  // Track if clearState has been processed to prevent multiple resets
  const clearStateProcessedRef = useRef(false);
  const resetStateRef = useRef(resetState);
  
  // Keep ref updated with latest resetState function
  resetStateRef.current = resetState;

  useEffect(() => {
    // Only process once when clearState flag is present and we haven't processed it yet
    if (location.state?.clearState === true && !clearStateProcessedRef.current) {
      clearStateProcessedRef.current = true;
      // Use setTimeout to ensure this runs after initial state is set
      setTimeout(() => {
        resetStateRef.current();
        // Clear the navigation state to prevent re-processing on re-renders
        window.history.replaceState({}, document.title, location.pathname);
      }, 0);
    }
    // Reset the ref when clearState flag is not present (allows processing on next navigation)
    if (!location.state?.clearState) {
      clearStateProcessedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state?.clearState]); // Only depend on location, not resetState
}





